// Utils
import { getCanAdministerShares } from 'modules/shares/utils'
import { hasPaymentDetails } from './payments'

// Types
import {
  GetBillingHistoryCountQuery,
  GetCustomerProviderQuery,
  GetWorkspaceSettingsQuery,
  WorkspaceMinimalFragment,
  GetCustomerQuery,
  useGetCustomerSignupSourceQuery,
  useGetCustomerProviderQuery,
} from '@sketch/gql-types'

type Customer = GetCustomerQuery['customer']

interface GetPermissionsArgs {
  billingHistoryCount?: GetBillingHistoryCountQuery
  customerProvider?: GetCustomerProviderQuery
  settings?: GetWorkspaceSettingsQuery['workspace']
  workspace: WorkspaceMinimalFragment
  customer?: Customer
  source?: string
}

export const getWorkspaceUserRolePermissions = (
  workspace: WorkspaceMinimalFragment
) => {
  const isFinance = workspace.userRole === 'FINANCE'
  const isPartner = workspace.userRole === 'PARTNER'
  const isAdmin = workspace.userRole === 'ADMIN'
  const isGuest = workspace.userRole === 'GUEST'

  const canManageBillingSetting = isAdmin || isFinance || isPartner

  return {
    isFinance,
    isPartner,
    isGuest,
    isAdmin,
    canManageBillingSetting,
  }
}

export const getWorkspaceTypePermissions = (
  workspace: WorkspaceMinimalFragment
) => {
  const isPersonalWorkspace = workspace.type === 'PERSONAL'
  const isEducationWorkspace = workspace.type === 'STUDENT'
  const isBusinessWorkspace = workspace.type === 'BUSINESS'

  return { isPersonalWorkspace, isEducationWorkspace, isBusinessWorkspace }
}

export const getWorkspaceSettingsSSOPermissions = (
  settings?: GetWorkspaceSettingsQuery['workspace']
) => {
  return !!settings?.features?.ssoEnabled
}

export const isAppStoreSubscription = (provider?: GetCustomerProviderQuery) => {
  const isAppStoreSubscription =
    provider?.customer.subscriptionInfo.__typename === 'AppStoreSubscription'

  return isAppStoreSubscription
}

export const isTrialing = (customerProvider?: GetCustomerProviderQuery) => {
  const status = customerProvider?.customer?.subscriptionInfo?.status
  return status === 'TRIALING'
}

export const getWorkspaceCustomerHasInvoices = (
  billingHistoryCount?: GetBillingHistoryCountQuery
) => {
  const hasInvoices = !!billingHistoryCount?.customer?.billingHistory.meta
    .totalCount
  return hasInvoices
}

const isAppStoreWorkspace = (source?: string | null) =>
  source === 'macappstore' || source === 'ios'

// Here we will group all the conditions for the cases where we can let the user subscribe via stripe and
// prevent showing the 'subscribe' button on the MAS app webview
const canSubscribeViaStripe = (
  source?: string,
  customerProvider?: GetCustomerProviderQuery
) => {
  // We won't let any workspace subscribed via App Store to subscribe via Stripe
  if (isAppStoreSubscription(customerProvider)) {
    return false
  }

  // If it's coming from 'macappstore' or 'ios' we don't let the user subscribe via stripe
  if (isAppStoreWorkspace(source)) {
    return false
  }

  // If it's not trialing on 'macappstore' or 'ios' means that it's coming from our app or the web, so we let subscribe via Stripe. Also,
  // we won't let them subscribe on Stripe while they are subscribed via App Store to prevent corner cases
  return true
}

export const useCanSubscribeViaStripe = (
  workspaceId: string,
  customerId?: string
) => {
  const {
    data: signupSource,
    loading: loadingSource,
  } = useGetCustomerSignupSourceQuery({
    variables: { identifier: workspaceId },
    skip: !workspaceId,
  })

  const {
    data: customerProvider,
    loading: loadingCustomer,
  } = useGetCustomerProviderQuery({
    variables: { customerId: customerId! },
    skip: !customerId,
  })

  const source = signupSource?.workspace?.customer?.source
  const loading = loadingSource || loadingCustomer

  return {
    canSubscribeStripe: canSubscribeViaStripe(source, customerProvider),
    source,
    customerProvider,
    loading,
  }
}

/**
 *
 * Aggregates a bunch of workspace permissions in a tidy object
 * to make it easier to use
 */
const workspaceSettingsPermissions = ({
  workspace,
  customerProvider,
  billingHistoryCount,
  settings,
  customer,
  source,
}: GetPermissionsArgs) => {
  const {
    isFinance,
    isGuest,
    isAdmin,
    isPartner,
    canManageBillingSetting,
  } = getWorkspaceUserRolePermissions(workspace)
  const {
    isEducationWorkspace,
    isPersonalWorkspace,
    isBusinessWorkspace,
  } = getWorkspaceTypePermissions(workspace)
  const isIOS = isAppStoreSubscription(customerProvider)
  const hasInvoices = getWorkspaceCustomerHasInvoices(billingHistoryCount)
  const canSubscribeStripe = canSubscribeViaStripe(source, customerProvider)
  const isSubscribed = hasPaymentDetails(customer?.paymentDetails)
  const isMASTrial = isTrialing(customerProvider) && isAppStoreWorkspace(source)

  const isAllowedBillingRouteForEducationWorkspace =
    !isEducationWorkspace || hasInvoices || isSubscribed

  const showWorkspaceSettingsGeneral =
    !isPersonalWorkspace &&
    isAdmin &&
    !isFinance &&
    !isIOS &&
    (!isEducationWorkspace || (isEducationWorkspace && isSubscribed))

  const showWorkspaceSettingsBilling =
    !isPersonalWorkspace &&
    canManageBillingSetting &&
    isAllowedBillingRouteForEducationWorkspace &&
    canSubscribeStripe

  const showWorkspaceSettingsSSO =
    !isPersonalWorkspace &&
    isAdmin &&
    !isIOS &&
    !isEducationWorkspace &&
    !isMASTrial

  const showWorkspaceSettingsPeople = !isMASTrial
  const showWorkspacePermissionGroup =
    isAdmin && isBusinessWorkspace && workspace.features.permissionGroupsEnabled

  const hideNavigation =
    isIOS ||
    (isEducationWorkspace && !isAllowedBillingRouteForEducationWorkspace)

  const isFinanceOrPartner = isFinance || isPartner
  const showDiscover = !isFinanceOrPartner
  const showCommunity = !isFinanceOrPartner

  const showUpsell =
    isAdmin && customer?.subscription?.currentPlan?.product === 'TEAMS'

  return {
    hideNavigation,
    isAdmin,
    isEducationWorkspace,
    isBusinessWorkspace,
    isFinance,
    isGuest,
    isIOS,
    isPartner,
    isPersonalWorkspace,
    showDiscover,
    showCommunity,
    showUpsell,
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    showWorkspaceSettingsSSO,
    showWorkspaceSettingsPeople,
    showWorkspacePermissionGroup,
  }
}

const showWorkspaceSettings = (workspace: WorkspaceMinimalFragment): boolean =>
  workspace.userRole !== 'GUEST'

const canUserAdministerShares = (
  workspace: WorkspaceMinimalFragment
): boolean => getCanAdministerShares(workspace)

export {
  showWorkspaceSettings,
  canUserAdministerShares,
  workspaceSettingsPermissions,
}
